/**
 * How long a timer has been running, and how a duration is written down.
 *
 * Pure, and deliberately in its own file: the number on the top bar and the number
 * on the Time Tracking page must be produced by **one** function, and a function
 * that lives inside either component is one the other cannot reach without
 * importing a component. `web/shapes/time-tracking.md` §5 calls this "one clock";
 * this file is the arithmetic half of it.
 *
 * ── The formula is the server's own, mirrored ───────────────────────────────
 *
 * `src/modules/timers/transitions.ts` `applyClock`: `duration_seconds` **accrues
 * closed worked intervals only**. The interval currently open — since `resumed_at`,
 * or since `started_at` if the entry has never been paused — is added when the next
 * transition writes. So:
 *
 *   running   duration_seconds + floor((now - (resumed_at ?? started_at)) / 1000)
 *   paused    duration_seconds                (the worked interval closed at the pause)
 *   anything  duration_seconds                (the entry is finished)
 *   else
 *
 * Two properties follow, and both are why this is not an accumulator ticking in
 * React state:
 *
 *  - **It equals what the server will record.** The moment the employee presses
 *    stop, `applyClock` computes exactly this sum against the database clock. A
 *    counter incremented once a second in the browser would drift — a backgrounded
 *    tab is throttled to one tick a minute — and would then disagree with the
 *    number that lands in the entry, in front of the person whose hours they are.
 *  - **It is recoverable.** Re-deriving from `started_at` on every render means a
 *    reload, a route change or a re-mount produces the same number, because the
 *    state it reads from is the entry the server sent and not something the
 *    previous render happened to have counted to.
 *
 * `Math.floor` rather than `Math.round`, matching `elapsedSeconds` in
 * `transitions.ts`, and negatives are clamped to zero for the same reason it does:
 * a client clock a few seconds behind the database must not render a negative
 * elapsed time.
 */

import type { TimeEntry } from '../api/types';

/** An entry that is still the employee's to control. */
export function isLiveEntry(entry: TimeEntry): boolean {
  return entry.status === 'running' || entry.status === 'paused';
}

function instantOf(iso: string | null | undefined): number | null {
  if (iso === null || iso === undefined) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Seconds this entry has been worked, as of `nowMs`.
 *
 * A `running` entry whose `started_at` will not parse contributes only its recorded
 * `duration_seconds` — never `NaN`, and never `now` measured from zero, which would
 * render as fifty-six years of work.
 */
export function elapsedSecondsOf(entry: TimeEntry, nowMs: number): number {
  const recorded = entry.duration_seconds ?? 0;
  if (entry.status !== 'running') return recorded;
  const openedAt = instantOf(entry.resumed_at) ?? instantOf(entry.started_at);
  if (openedAt === null) return recorded;
  const open = Math.floor((nowMs - openedAt) / 1000);
  return recorded + (open > 0 ? open : 0);
}

/**
 * `1:12:40` — hours, minutes, seconds, for a live timer.
 *
 * Hours are not padded and are not capped at 24: a timer that has run for thirty
 * hours says `30:00:00`, because wrapping it to `06:00:00` would render an
 * unstopped timer as a plausible morning's work.
 */
export function formatClock(totalSeconds: number): string {
  const safe = Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.floor(totalSeconds) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${String(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * `8 g 12 ph` / `8 h 12 m` — a duration a person reads rather than counts.
 *
 * Seconds are dropped on purpose: an attendance figure quoted to the second implies
 * a precision the union-of-devices rollup does not have. Zero is rendered as
 * `0 ph`, and it is the caller's job never to reach this function with a figure
 * that is really an absence — `PRODUCT.md` §4's seventh row is about exactly that,
 * and it is why `AgentOffline` takes no number at all.
 */
export function formatDurationMinutes(
  totalMinutes: number,
  labels: { readonly hour: string; readonly minute: string },
): string {
  const safe = Number.isFinite(totalMinutes) && totalMinutes > 0 ? Math.round(totalMinutes) : 0;
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;
  if (hours === 0) return `${String(minutes)} ${labels.minute}`;
  return `${String(hours)} ${labels.hour} ${String(minutes)} ${labels.minute}`;
}

/** The same, from seconds — what the aggregate rollup speaks. */
export function formatDurationSeconds(
  totalSeconds: number,
  labels: { readonly hour: string; readonly minute: string },
): string {
  const safe = Number.isFinite(totalSeconds) && totalSeconds > 0 ? totalSeconds : 0;
  return formatDurationMinutes(safe / 60, labels);
}

/**
 * How long a timer may run before the screen says so, in seconds.
 *
 * **This is a display threshold and is not `wp_policies.timer_auto_stop_minutes`.**
 * That column exists (migration 015) and no session endpoint publishes it, so the
 * client cannot know a tenant's value and must not imply one: the copy this
 * threshold triggers states an elapsed fact ("this timer has been running for more
 * than eight hours") and makes no claim about what the company's policy will do.
 * Eight hours because that is a full shift; nothing depends on the exact number.
 */
export const LONG_RUNNING_SECONDS = 8 * 3600;
